import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { EmptyState, Loading } from "../../components/shared";
import { toast } from "sonner";
import { MapPinned, Trash2 } from "lucide-react";

const INITIAL_AREA = { state: "", district: "", city: "", locality: "", pin_code: "", service_id: "" };

export default function VendorServiceAreas() {
  const [areas, setAreas] = useState(null);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState(INITIAL_AREA);
  const [saving, setSaving] = useState(false);
  const load = () => {
    api.get("/vendor/service-areas").then((response) => setAreas(response.data));
    api.get("/vendor/services").then((response) => setServices(response.data));
  };

  useEffect(load, []);
  if (!areas) return <Loading />;
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const add = async () => {
    setSaving(true);
    try {
      await api.post("/vendor/service-areas", form);
      setForm(INITIAL_AREA);
      load();
      toast.success("Service area added");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not add service area");
    } finally {
      setSaving(false);
    }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/vendor/service-areas/${id}`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not remove service area");
    }
  };

  return (
    <section data-testid="vendor-service-areas-page">
      <div className="border-b border-slate-200 pb-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Coverage</p>
        <h1 className="mt-2 font-display text-3xl font-bold">Service areas</h1>
        <p className="mt-2 text-sm text-slate-600">Add the locations where your business can provide services.</p>
      </div>
      <div className="mt-6 border border-slate-200 bg-white p-5 sm:p-7">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <AreaInput label="State *" name="state" set={set} value={form.state} />
          <AreaInput label="District *" name="district" set={set} value={form.district} />
          <AreaInput label="City *" name="city" set={set} value={form.city} />
          <AreaInput label="Locality" name="locality" set={set} value={form.locality} />
          <AreaInput label="PIN Code" name="pin_code" set={set} value={form.pin_code} />
          <div>
            <Label className="mb-1.5 block" htmlFor="vendor-area-service">Coverage for</Label>
            <select
              className="h-10 w-full border border-slate-300 bg-white px-3 text-sm"
              data-testid="vendor-area-service"
              id="vendor-area-service"
              onChange={(event) => set("service_id", event.target.value)}
              value={form.service_id}
            >
              <option value="">All selected services</option>
              {services.filter((item) => item.status === "ACTIVE").map((item) => (
                <option key={item.service_id} value={item.service_id}>{item.service.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button data-testid="vendor-area-add-button" disabled={saving} onClick={add} type="button">
            {saving ? "Adding…" : "Add service area"}
          </Button>
        </div>
      </div>
      <div className="mt-6 space-y-3">
        {areas.length === 0 ? <EmptyState icon={MapPinned} subtitle="Add the locations where you provide services." title="No service areas" /> : areas.map((area) => (
          <article className="flex flex-wrap items-center justify-between gap-4 border border-slate-200 bg-white p-5" data-testid={`vendor-area-${area.vendor_service_area_id}`} key={area.vendor_service_area_id}>
            <div>
              <p className="font-semibold">{[area.locality, area.city, area.district, area.state].filter(Boolean).join(", ")}</p>
              <p className="mt-1 text-sm text-slate-500">{area.service_name}{area.pin_code ? ` · ${area.pin_code}` : ""}</p>
            </div>
            <button
              className="inline-flex h-9 w-9 items-center justify-center text-rose-700 hover:bg-rose-50"
              data-testid={`vendor-area-delete-${area.vendor_service_area_id}`}
              onClick={() => remove(area.vendor_service_area_id)}
              title="Remove service area"
              type="button"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}

function AreaInput({ label, name, set, value }) {
  return (
    <div>
      <Label className="mb-1.5 block" htmlFor={`vendor-area-${name}`}>{label}</Label>
      <Input
        data-testid={`vendor-area-${name}`}
        id={`vendor-area-${name}`}
        onChange={(event) => set(name, event.target.value)}
        value={value}
      />
    </div>
  );
}